import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { queryKeys } from '@/lib/queryClient'
import { useAuthStore } from '@/stores/authStore'
import type {
  MenuCategory,
  MenuItem,
  MenuItemAddon,
  MenuItemStatus,
  SourceType,
  Ingredient,
  IngredientSupplier,
  Supplier,
  SupplierCatalogItem,
  Recipe,
  Procedure,
  ProcedureStep,
} from '@/types/app'

export interface MenuCategoryGroup<T> {
  cat: { id: string; name: string }
  items: T[]
}

// Groups items by category for the catering picker/matrix/pricing views —
// every one of those was silently dropping any item with no category (or an
// orphaned one), which in practice meant every accompaniment/add-on, since
// those are typically left uncategorized. Add-ons get their own "Add-ons"
// bucket; anything else uncategorized falls into "Other" — nothing vanishes.
export function groupMenuItemsByCategory<T extends { category_id: string | null; is_addon: boolean }>(
  items: T[],
  categories: Pick<MenuCategory, 'id' | 'name' | 'display_order'>[],
): MenuCategoryGroup<T>[] {
  const sorted = [...categories].sort((a, b) => a.display_order - b.display_order)
  const groups: MenuCategoryGroup<T>[] = sorted
    .map(cat => ({ cat: { id: cat.id, name: cat.name }, items: items.filter(i => i.category_id === cat.id) }))
    .filter(g => g.items.length > 0)

  const knownIds = new Set(categories.map(c => c.id))
  const uncategorized = items.filter(i => !i.category_id || !knownIds.has(i.category_id))
  const addons = uncategorized.filter(i => i.is_addon)
  const others = uncategorized.filter(i => !i.is_addon)
  if (addons.length > 0) groups.push({ cat: { id: '__addons__', name: 'Add-ons' }, items: addons })
  if (others.length > 0) groups.push({ cat: { id: '__other__', name: 'Other' }, items: others })
  return groups
}

export function useMenuCategories() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')

  return useQuery({
    queryKey: queryKeys.menuCategories(restaurantId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('menu_categories')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .eq('is_active', true)
        .order('display_order', { ascending: true })
      if (error) throw error
      return (data ?? []) as MenuCategory[]
    },
    enabled: !!restaurantId,
  })
}

// Create (no id), rename, reorder (display_order), or soft-remove
// (is_active: false) a category — every restaurant names/organizes their
// menu differently, so this has to be self-service, not a support ticket.
// Soft-remove rather than a hard delete: existing products keep their
// category_id (just hidden from active lists) instead of risking an FK error
// or silently orphaning products that were in the deleted category.
export function useSaveMenuCategory() {
  const qc = useQueryClient()
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')

  return useMutation({
    mutationFn: async (category: { id?: string; name?: string; display_order?: number; is_active?: boolean }) => {
      if (category.id) {
        const { id, ...rest } = category
        const { data, error } = await supabase
          .from('menu_categories').update(rest).eq('id', id).select().single()
        if (error) throw error
        return data as MenuCategory
      }
      const { data, error } = await supabase
        .from('menu_categories')
        .insert({ ...category, restaurant_id: restaurantId })
        .select().single()
      if (error) throw error
      return data as MenuCategory
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.menuCategories(restaurantId) }),
  })
}

export function useMenuItems(categoryId?: string) {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')

  return useQuery({
    queryKey: [...queryKeys.menuItems(restaurantId), categoryId ?? 'all'],
    queryFn: async () => {
      let query = supabase
        .from('menu_items')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .eq('is_deleted', false)
        .eq('is_addon', false)
        .order('sort_order', { ascending: true })

      if (categoryId) {
        query = query.eq('category_id', categoryId)
      }

      const { data, error } = await query
      if (error) throw error
      return (data ?? []) as MenuItem[]
    },
    enabled: !!restaurantId,
  })
}

export function useMenuItem(id: string) {
  return useQuery({
    queryKey: queryKeys.menuItem(id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('menu_items')
        .select('*')
        .eq('id', id)
        .single()
      if (error) throw error
      return data as MenuItem
    },
    enabled: !!id,
  })
}

export function useAddons() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  return useQuery({
    queryKey: queryKeys.addons(restaurantId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('menu_items')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .eq('is_addon', true)
        .eq('is_deleted', false)
        .order('name', { ascending: true })
      if (error) throw error
      return (data ?? []) as MenuItem[]
    },
    enabled: !!restaurantId,
  })
}

export function useMenuItemAddons(menuItemId: string) {
  return useQuery({
    queryKey: queryKeys.menuItemAddons(menuItemId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('menu_item_addons')
        .select('*, addon:menu_items!addon_id(id, name)')
        .eq('menu_item_id', menuItemId)
        .order('sort_order', { ascending: true })
      if (error) throw error
      return (data ?? []) as (MenuItemAddon & { addon: { id: string; name: string } | null })[]
    },
    enabled: !!menuItemId,
  })
}

export function useSaveMenuItemAddons() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ menuItemId, addonIds }: { menuItemId: string; addonIds: string[] }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (supabase.from('menu_item_addons') as any).delete().eq('menu_item_id', menuItemId)
      if (addonIds.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase.from('menu_item_addons') as any).insert(
          addonIds.map((addon_id, i) => ({ menu_item_id: menuItemId, addon_id, restaurant_id: restaurantId, sort_order: i }))
        )
        if (error) throw error
      }
    },
    onSuccess: (_, { menuItemId }) => {
      qc.invalidateQueries({ queryKey: queryKeys.menuItemAddons(menuItemId) })
    },
  })
}

export function useIngredients() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')

  return useQuery({
    queryKey: queryKeys.ingredients(restaurantId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ingredients')
        .select('*, ingredient_suppliers(*, supplier:suppliers(*))')
        .eq('restaurant_id', restaurantId)
        .eq('is_deleted', false)
        .order('name', { ascending: true })
      if (error) throw error
      return (data ?? []) as (Ingredient & {
        ingredient_suppliers: Array<{
          id: string
          ingredient_id: string
          supplier_id: string
          is_preferred: boolean
          unit_price: number | null
          notes: string | null
          supplier: Supplier | null
        }>
      })[]
    },
    enabled: !!restaurantId,
  })
}

export function useSuppliers() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')

  return useQuery({
    queryKey: queryKeys.suppliers(restaurantId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .order('name', { ascending: true })
      if (error) throw error
      return (data ?? []) as Supplier[]
    },
    enabled: !!restaurantId,
  })
}

export function useSaveSupplier() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: Partial<Supplier> & { id?: string }) => {
      const { id, ...rest } = payload
      if (id) {
        const { data, error } = await supabase
          .from('suppliers')
          .update({ ...rest, updated_at: new Date().toISOString() } as Record<string, unknown>)
          .eq('id', id)
          .select()
          .single()
        if (error) throw error
        return data as Supplier
      } else {
        const { data, error } = await supabase
          .from('suppliers')
          .insert({ ...rest, restaurant_id: restaurantId } as Record<string, unknown>)
          .select()
          .single()
        if (error) throw error
        return data as Supplier
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.suppliers(restaurantId) }),
  })
}

export function useRecipe(menuItemId: string) {
  return useQuery({
    queryKey: queryKeys.recipe(menuItemId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('recipes')
        .select('*, recipe_ingredients(*, ingredient:ingredients(*))')
        .eq('menu_item_id', menuItemId)
        .eq('is_current', true)
        .maybeSingle()
      if (error) throw error
      return data as Recipe | null
    },
    enabled: !!menuItemId,
  })
}

export function useProcedure(menuItemId: string) {
  return useQuery({
    queryKey: queryKeys.procedure(menuItemId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('procedures')
        .select('*')
        .eq('menu_item_id', menuItemId)
        .eq('is_current', true)
        .maybeSingle()
      if (error) throw error
      return data as Procedure | null
    },
    enabled: !!menuItemId,
  })
}

export function useSaveMenuItem() {
  const qc = useQueryClient()
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')

  return useMutation({
    mutationFn: async (item: Partial<MenuItem> & { id?: string }) => {
      if (item.id) {
        const { id, ...rest } = item
        const { data, error } = await supabase
          .from('menu_items')
          .update({ ...rest, updated_at: new Date().toISOString() } as Record<string, unknown>)
          .eq('id', id)
          .select()
          .single()
        if (error) throw error
        return data as MenuItem
      } else {
        const { data, error } = await supabase
          .from('menu_items')
          .insert({ ...item, restaurant_id: restaurantId } as Record<string, unknown>)
          .select()
          .single()
        if (error) throw error
        return data as MenuItem
      }
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.menuItems(restaurantId) })
      qc.invalidateQueries({ queryKey: queryKeys.menuItem(data.id) })
    },
  })
}

// ── Bulk product import (Excel/CSV) ─────────────────────────────────────────

export interface ImportProductRow {
  category: string
  name: string
  regional_name?: string | null
  description?: string | null
  upc_code?: string | null
  price?: number | null
  wholesale_price?: number | null
  status?: MenuItemStatus | null
  source_type?: SourceType | null
  available_in_store?: boolean | null
  available_catering?: boolean | null
  available_store_supply?: boolean | null
  is_addon?: boolean | null
  per_piece_price?: number | null
  min_pieces?: number | null
  half_tray_price?: number | null
  half_tray_serves?: number | null
  half_tray_pieces?: number | null
  medium_tray_price?: number | null
  medium_tray_serves?: number | null
  medium_tray_pieces?: number | null
  full_tray_price?: number | null
  full_tray_serves?: number | null
  full_tray_pieces?: number | null
}

export interface ImportProductsResult {
  categoriesCreated: number
  itemsCreated: number
  cateringPricesSet: number
}

export function useBulkImportProducts() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (rows: ImportProductRow[]): Promise<ImportProductsResult> => {
      if (rows.length === 0) return { categoriesCreated: 0, itemsCreated: 0, cateringPricesSet: 0 }

      // 1. Resolve categories — reuse existing ones by name (case-insensitive), create the rest.
      const { data: existingCats, error: catErr } = await supabase
        .from('menu_categories')
        .select('id, name, display_order')
        .eq('restaurant_id', restaurantId)
      if (catErr) throw catErr

      const catByName = new Map<string, string>()
      let maxOrder = 0
      for (const c of existingCats ?? []) {
        catByName.set(c.name.trim().toLowerCase(), c.id)
        maxOrder = Math.max(maxOrder, c.display_order ?? 0)
      }

      const neededCatNames = [...new Set(
        rows.map(r => r.category.trim()).filter(Boolean)
      )].filter(name => !catByName.has(name.toLowerCase()))

      let categoriesCreated = 0
      if (neededCatNames.length > 0) {
        const { data: newCats, error: insCatErr } = await supabase
          .from('menu_categories')
          .insert(neededCatNames.map((name, i) => ({
            restaurant_id: restaurantId,
            name,
            display_order: maxOrder + i + 1,
          })) as Record<string, unknown>[])
          .select('id, name')
        if (insCatErr) throw insCatErr
        for (const c of newCats ?? []) catByName.set(c.name.trim().toLowerCase(), c.id)
        categoriesCreated = newCats?.length ?? 0
      }

      // 2. Insert menu items. available_catering defaults to "on" when any tray/per-piece
      //    price is present, but an explicit value in the sheet always wins.
      const hasCateringData = (r: ImportProductRow) =>
        [r.per_piece_price, r.half_tray_price, r.medium_tray_price, r.full_tray_price].some(v => v != null)

      const itemPayload = rows.map(r => ({
        restaurant_id: restaurantId,
        category_id: catByName.get(r.category.trim().toLowerCase()) ?? null,
        name: r.name.trim(),
        regional_name: r.regional_name?.trim() || null,
        description: r.description?.trim() || null,
        upc_code: r.upc_code?.trim() || null,
        price: r.price ?? 0,
        wholesale_price: r.wholesale_price ?? null,
        status: r.status ?? 'active',
        source_type: r.source_type ?? 'inhouse',
        available_in_store: r.available_in_store ?? true,
        available_catering: r.available_catering ?? hasCateringData(r),
        available_store_supply: r.available_store_supply ?? false,
        is_addon: r.is_addon ?? false,
      }))

      const { data: newItems, error: itemErr } = await supabase
        .from('menu_items')
        .insert(itemPayload as Record<string, unknown>[])
        .select('id, name')
      if (itemErr) throw itemErr

      // 3. Catering pricing — one row per item that carried any tray/per-piece/serves/pieces data.
      let cateringPricesSet = 0
      if (newItems && newItems.length === rows.length) {
        const pricingRows = rows
          .map((r, i) => ({ r, id: newItems[i].id }))
          .filter(({ r }) => [
            r.per_piece_price, r.half_tray_price, r.medium_tray_price, r.full_tray_price,
            r.half_tray_serves, r.medium_tray_serves, r.full_tray_serves,
            r.half_tray_pieces, r.medium_tray_pieces, r.full_tray_pieces, r.min_pieces,
          ].some(v => v != null))
          .map(({ r, id }) => ({
            restaurant_id: restaurantId,
            menu_item_id: id,
            per_piece_price: r.per_piece_price ?? null,
            min_pieces: r.min_pieces ?? null,
            half_tray_price: r.half_tray_price ?? null,
            half_tray_serves: r.half_tray_serves ?? null,
            half_tray_pieces: r.half_tray_pieces ?? null,
            medium_tray_price: r.medium_tray_price ?? null,
            medium_tray_serves: r.medium_tray_serves ?? null,
            medium_tray_pieces: r.medium_tray_pieces ?? null,
            full_tray_price: r.full_tray_price ?? null,
            full_tray_serves: r.full_tray_serves ?? null,
            full_tray_pieces: r.full_tray_pieces ?? null,
          }))
        if (pricingRows.length > 0) {
          const { error: priceErr } = await supabase
            .from('catering_item_pricing')
            .insert(pricingRows as Record<string, unknown>[])
          if (priceErr) throw priceErr
          cateringPricesSet = pricingRows.length
        }
      }

      return { categoriesCreated, itemsCreated: newItems?.length ?? 0, cateringPricesSet }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.menuItems(restaurantId) })
      qc.invalidateQueries({ queryKey: queryKeys.menuCategories(restaurantId) })
      qc.invalidateQueries({ queryKey: queryKeys.cateringPricing(restaurantId) })
    },
  })
}

type SupplierLink = Pick<IngredientSupplier, 'supplier_id' | 'is_preferred' | 'unit_price' | 'notes'>
type IngredientPayload = Partial<Ingredient> & { id?: string; supplierLinks?: SupplierLink[] }

export function useSaveIngredient() {
  const qc = useQueryClient()
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')

  return useMutation({
    mutationFn: async ({ supplierLinks, ...ingredient }: IngredientPayload) => {
      let ingredientId: string

      if (ingredient.id) {
        const { id, ...rest } = ingredient
        const { error } = await supabase
          .from('ingredients')
          .update(rest as Record<string, unknown>)
          .eq('id', id)
        if (error) throw error
        ingredientId = id!
      } else {
        const { data, error } = await supabase
          .from('ingredients')
          .insert({ ...ingredient, restaurant_id: restaurantId } as Record<string, unknown>)
          .select('id')
          .single()
        if (error) throw error
        ingredientId = data.id
      }

      // Sync supplier links atomically: delete all then re-insert
      if (supplierLinks !== undefined) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (supabase.from('ingredient_suppliers') as any).delete().eq('ingredient_id', ingredientId)
        if (supplierLinks.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { error } = await (supabase.from('ingredient_suppliers') as any).insert(
            supplierLinks.map(l => ({ ...l, ingredient_id: ingredientId }))
          )
          if (error) throw error
        }
        // Keep primary supplier_id in sync with preferred
        const preferred = supplierLinks.find(l => l.is_preferred)
        await supabase
          .from('ingredients')
          .update({ supplier_id: preferred?.supplier_id ?? null } as Record<string, unknown>)
          .eq('id', ingredientId)
      }

      return ingredientId
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.ingredients(restaurantId) })
    },
  })
}

interface UpsertRecipePayload {
  menu_item_id: string
  batch_size: number
  notes: string
  ingredients: Array<{
    ingredient_id: string
    quantity_per_serving: number
    unit: string
    yield_percentage: number
    notes: string
  }>
}

export function useUpsertRecipe() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (payload: UpsertRecipePayload) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)('upsert_recipe_ingredients', {
        p_menu_item_id: payload.menu_item_id,
        p_batch_size: payload.batch_size,
        p_notes: payload.notes,
        p_ingredients: payload.ingredients,
      })
      if (error) throw error
      return data as string
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.recipe(variables.menu_item_id) })
    },
  })
}

interface UpsertProcedurePayload {
  menu_item_id: string
  steps: ProcedureStep[]
}

export function useUpsertProcedure() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: async (payload: UpsertProcedurePayload) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)('upsert_procedure', {
        p_menu_item_id: payload.menu_item_id,
        p_steps: payload.steps,
      })
      if (error) throw error
      return data as string
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: queryKeys.procedure(variables.menu_item_id) })
    },
  })
}

export function useDeleteMenuItem() {
  const qc = useQueryClient()
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('menu_items')
        .update({ is_deleted: true } as Record<string, unknown>)
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.menuItems(restaurantId) })
    },
  })
}

export function useAllSupplierCatalog() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  return useQuery({
    queryKey: queryKeys.supplierCatalogAll(restaurantId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('supplier_catalog')
        .select('*, supplier:suppliers(id, name)')
        .eq('restaurant_id', restaurantId)
        .order('item_name', { ascending: true })
      if (error) throw error
      return (data ?? []) as (SupplierCatalogItem & { supplier: { id: string; name: string } | null })[]
    },
    enabled: !!restaurantId,
  })
}

export function useSupplierCatalog(supplierId: string) {
  return useQuery({
    queryKey: queryKeys.supplierCatalog(supplierId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('supplier_catalog')
        .select('*')
        .eq('supplier_id', supplierId)
        .order('item_name', { ascending: true })
      if (error) throw error
      return (data ?? []) as SupplierCatalogItem[]
    },
    enabled: !!supplierId,
  })
}

export function useSaveSupplierCatalogItem() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (item: Partial<SupplierCatalogItem> & { supplier_id: string }) => {
      const { id, ...rest } = item
      if (id) {
        const { data, error } = await supabase
          .from('supplier_catalog')
          .update(rest as Record<string, unknown>)
          .eq('id', id)
          .select()
          .single()
        if (error) throw error
        return data as SupplierCatalogItem
      }
      const { data, error } = await supabase
        .from('supplier_catalog')
        .insert({ ...rest, restaurant_id: restaurantId } as Record<string, unknown>)
        .select()
        .single()
      if (error) throw error
      return data as SupplierCatalogItem
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.supplierCatalog(data.supplier_id) })
    },
  })
}

export function useImageUpload() {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string

  return useMutation({
    mutationFn: async ({ file, menuItemId }: { file: File; menuItemId?: string }) => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Not authenticated')

      const res = await fetch(`${supabaseUrl}/functions/v1/upload-image`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filename: file.name,
          content_type: file.type,
          menu_item_id: menuItemId,
        }),
      })

      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as { error?: string }
        throw new Error(err.error ?? `Failed to get upload URL (${res.status})`)
      }

      const { upload_url, public_url } = await res.json() as { upload_url: string; public_url: string }

      const putRes = await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      })

      if (!putRes.ok) throw new Error(`Upload to R2 failed (${putRes.status})`)

      return public_url
    },
  })
}

export function useDeleteSupplierCatalogItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, supplierId }: { id: string; supplierId: string }) => {
      const { error } = await supabase.from('supplier_catalog').delete().eq('id', id)
      if (error) throw error
      return supplierId
    },
    onSuccess: (supplierId) => {
      qc.invalidateQueries({ queryKey: queryKeys.supplierCatalog(supplierId) })
    },
  })
}
